import { useRef, useState } from "react";
import type { IInsImage } from "@/store/insurance";
import { WebcamCapture } from "./WebcamCapture";

export interface SelectedImage {
  /** Full data URL for immediate preview. */
  dataUrl: string;
  /** Base64 payload without the `data:...;base64,` prefix (what the backend stores). */
  base64Raw: string;
  /** File extension, e.g. "jpg" / "png" / "pdf". */
  extension: string;
}

interface Props {
  side: "front" | "back";
  image?: IInsImage;
  /** Disabled until the insurance record exists (needs an id to attach the file to). */
  disabled?: boolean;
  busy?: boolean;
  onSelect: (img: SelectedImage) => void;
}

/** Images (any type) plus PDF — some carriers issue cards as a PDF. */
const ACCEPT_FILES = "image/*,application/pdf";

function extensionFromFile(file: File): string {
  const fromName = file.name.includes(".") ? file.name.split(".").pop() : "";
  if (fromName) return fromName.toLowerCase();
  const sub = file.type.split("/")[1];
  return (sub || "jpg").toLowerCase();
}

/**
 * Phones and tablets (coarse pointer) open the camera from a file input's `capture` attribute;
 * computers ignore it and show a file browser, so they get the in-page webcam instead.
 */
function cameraOpensFromFileInput(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
}

function isPdfSource(extension?: string, dataUrl?: string): boolean {
  if (extension && extension.toLowerCase() === "pdf") return true;
  return !!dataUrl && dataUrl.startsWith("data:application/pdf");
}

/**
 * Insurance-card tile. Tap to choose how to add the card: take a photo (camera),
 * or pick from photos/files — the latter also accepts a PDF. Optional; a patient
 * can skip it. Shows a thumbnail for images and a PDF badge for PDFs.
 */
export function InsuranceCardUpload({ side, image, disabled, busy, onSelect }: Props) {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const label = side === "front" ? "Front of card" : "Back of card";
  const preview = image?.base64 || "";
  const previewIsPdf = isPdfSource(image?.extension, preview);

  const openMenu = () => {
    if (disabled || busy) return;
    setError(null);
    setMenuOpen(true);
  };

  const openCamera = () => {
    setMenuOpen(false);
    if (cameraOpensFromFileInput()) cameraInputRef.current?.click();
    else setWebcamOpen(true);
  };

  const openFiles = () => {
    setMenuOpen(false);
    fileInputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf" || extensionFromFile(file) === "pdf";
    if (!isImage && !isPdf) {
      setError("Please choose an image or a PDF.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64Raw = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      onSelect({ dataUrl, base64Raw, extension: extensionFromFile(file) });
    };
    reader.onerror = () => setError("Could not read that file.");
    reader.readAsDataURL(file);
  };

  return (
    <div className="relative flex flex-col">
      <button
        type="button"
        onClick={openMenu}
        disabled={disabled || busy}
        aria-label={`${label} — add photo or PDF`}
        className="relative flex h-32 w-full items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 transition-colors hover:border-healthsource-navy disabled:cursor-not-allowed disabled:opacity-60"
      >
        {preview && previewIsPdf ? (
          <span className="flex flex-col items-center gap-1 text-[13px] font-semibold text-healthsource-dark">
            <i className="fas fa-file-pdf text-2xl text-healthsource-navy" />
            {busy ? "Uploading…" : "PDF attached"}
          </span>
        ) : preview ? (
          <img src={preview} alt={label} className="h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-1 text-[13px] font-semibold">
            <i className="fas fa-camera text-lg" />
            {busy ? "Uploading…" : label}
          </span>
        )}
      </button>
      <span className="mt-1 text-center text-[13px] text-healthsource-gray">
        {preview ? `${label} — tap to replace` : disabled ? "Enter details first" : "Photo or PDF · optional"}
      </span>
      {error && <span className="mt-1 text-center text-[13px] font-semibold text-red-600">{error}</span>}

      {menuOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => setMenuOpen(false)}
        >
          <div
            className="w-full max-w-sm overflow-hidden rounded-t-2xl bg-white p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-lg sm:rounded-2xl sm:pb-2"
            style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="px-3 py-2 text-[15px] font-extrabold text-healthsource-dark">
              Add {label.toLowerCase()}
            </p>
            <button
              type="button"
              onClick={openCamera}
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-[15px] font-semibold text-healthsource-dark transition-colors hover:bg-slate-50"
            >
              <i className="fas fa-camera w-5 text-healthsource-navy" />
              Take a photo
            </button>
            <button
              type="button"
              onClick={openFiles}
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-[15px] font-semibold text-healthsource-dark transition-colors hover:bg-slate-50"
            >
              <i className="fas fa-folder-open w-5 text-healthsource-navy" />
              Choose photo, file, or PDF
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="mt-1 flex min-h-11 w-full items-center justify-center rounded-lg border-t border-slate-100 px-3 py-3 text-[15px] font-bold text-healthsource-gray transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {webcamOpen && (
        <WebcamCapture
          title={`Take a photo of the ${label.toLowerCase()}`}
          onClose={() => setWebcamOpen(false)}
          onCapture={(img) => {
            setWebcamOpen(false);
            onSelect(img);
          }}
        />
      )}

      {/* Camera directly (rear camera on mobile). */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onChange}
      />
      {/* Photos / files, including PDF — no `capture`, so the OS offers Photos and Files. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_FILES}
        className="hidden"
        onChange={onChange}
      />
    </div>
  );
}
